<template>
  <v-container fluid class="pa-4">
    <!-- Header -->
    <div>
      <span class="section-subtitle text-uppercase text-danger fw-semibold d-block mb-2">My Dashboard</span>
      <h2 class="section-title fw-bold mb-4">
        Welcome, {{ user?.first_name || 'Guest' }} {{ user?.last_name || '' }}
      </h2>
    </div>

    <!-- Statistics Cards -->
    <v-row dense class="mb-6">
      <!-- Unread Notifications -->
      <v-col cols="12" md="3">
        <v-card variant="tonal" color="red" elevation="2" class="stat-card h-100">
          <v-card-text class="py-5 d-flex align-center justify-space-between">
            <div>
              <div class="text-overline text-medium-emphasis">Unread Notifications</div>
              <div class="text-h5 font-weight-bold text-red-darken-2">
                {{ summary.unread_notifications }}
              </div>
            </div>
            <v-avatar size="56" color="red" variant="tonal" class="shadow-sm">
              <v-icon size="34">mdi-bell-ring-outline</v-icon>
            </v-avatar>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Listings -->
      <v-col cols="12" md="3">
        <v-card variant="tonal" color="blue" elevation="2" class="stat-card h-100">
          <v-card-text class="py-5 d-flex align-center justify-space-between">
            <div>
              <div class="text-overline text-medium-emphasis">Listings</div>
              <div class="text-h5 font-weight-bold text-blue-darken-2">
                {{ summary.listing_count }}
              </div>
            </div>
            <v-avatar size="56" color="blue" variant="tonal" class="shadow-sm">
              <v-icon size="34">mdi-view-list-outline</v-icon>
            </v-avatar>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Total Paid to Agent -->
      <v-col cols="12" md="3">
        <v-card variant="tonal" color="teal" elevation="2" class="stat-card h-100">
          <v-card-text class="py-5 d-flex align-center justify-space-between">
            <div>
              <div class="text-overline text-medium-emphasis">Total Paid to Agent</div>
              <div class="text-h5 font-weight-bold text-teal-darken-2">
                {{ formatCurrency(summary.total_payout) }}
              </div>
            </div>
            <v-avatar size="56" color="teal" variant="tonal" class="shadow-sm">
              <v-icon size="34">mdi-cash-multiple</v-icon>
            </v-avatar>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Messages -->
      <v-col cols="12" md="3">
        <v-card variant="tonal" color="indigo" elevation="2" class="stat-card h-100">
          <v-card-text class="py-5 d-flex align-center justify-space-between">
            <div>
              <div class="text-overline text-medium-emphasis">Messages</div>
              <div class="text-h5 font-weight-bold text-indigo-darken-2">
                {{ summary.unread_messages }}
              </div>
            </div>
            <v-avatar size="56" color="indigo" variant="tonal" class="shadow-sm">
              <v-icon size="34">mdi-message-text-outline</v-icon>
            </v-avatar>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Listings (Agents only) -->
    <v-card v-if="isAgent" elevation="2" class="mb-6">
      <v-card-title class="d-flex align-center justify-space-between">
        <span>Current Listings</span>
        <v-progress-linear
          v-if="isLoadingListings"
          indeterminate
          color="primary"
          height="4"
          rounded
          style="max-width: 180px;"
        />
      </v-card-title>

      <v-card-text>
        <v-table density="comfortable" class="rounded-lg elevation-1">
          <thead>
            <tr class="text-center">
              <th class="text-center">Cruise Line</th>
              <th class="text-center">Ship</th>
              <th class="text-center">Itinerary Name</th>
              <th class="text-center">Sail Date</th>
              <th class="text-center">Total Cost</th>
              <th class="text-center">Service Fee</th>
              <th class="text-center">Payout Amount</th>
              <th class="text-center">Created Date</th>
              <th class="text-center">Step</th>
              <th class="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="l in listings" :key="l.id" class="text-center">
              <td>{{ l.itinerary?.ship?.cruiseLine ?? '—' }}</td>
              <td>{{ l.itinerary?.ship?.name ?? '—' }}</td>
              <td>{{ l.itinerary?.title ?? '—' }}</td>
              <td>{{ formatDate(l.itinerary?.date) }}</td>

              <td class="text-right">{{ formatCurrency(l.total_cost) }}</td>
              <td class="text-right">{{ formatCurrency(l.service_fee) }}</td>
              <td class="text-right">{{ formatCurrency(payoutAmount(l.total_cost, l.service_fee)) }}</td>

              <td>{{ formatDate(l.created_at) }}</td>

              <!-- Current Step pill -->
              <td>
                <v-chip
                  v-bind="chipAttrsFor(l)"
                  size="small"
                  class="font-weight-medium w-100 text-center"
                  variant="flat"
                >
                  <v-icon :icon="stepFor(l).icon" start class="mr-2" />
                  {{ stepFor(l).label }}
                </v-chip>
              </td>

              <td class="text-center">
                <v-btn :to="`/dashboard/listings/${l.id}`" color="secondary" variant="outlined" size="small" >
                  View
                </v-btn>
              </td>
            </tr>

            <tr v-if="!isLoadingListings && listings.length === 0">
              <td colspan="10" class="text-center text-medium-emphasis py-6">
                No listings found.
              </td>
            </tr>
          </tbody>
        </v-table>

        <!-- Pagination -->
        <v-row class="mt-4" justify="space-between" align="center">
          <v-col cols="auto">
            <small class="text-medium-emphasis">
              Showing {{ pageStart + 1 }}–{{ pageEnd }} of {{ totalCount }}
            </small>
          </v-col>

          <v-col cols="auto" class="d-flex align-center" style="gap: 12px;">
            <v-pagination
              v-model="currentPage"
              :length="totalPages"
              :total-visible="5"
              prev-icon="mdi-chevron-left"
              next-icon="mdi-chevron-right"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { supabase } from '@/supabaseClient'

/* ---------- user from store ---------- */
const store = useStore()
const user = computed(() => store.state.user || null)
const isAgent = computed(() => !!user.value?.is_agent)

/* ---------- stats summary ---------- */
const summary = ref({
  unread_notifications: 0,
  listing_count: 0,
  total_payout: 0,
  unread_messages: 0
})
const isLoadingSummary = ref(false)

async function loadSummary() {
  if (!user.value?.uid) return
  try {
    isLoadingSummary.value = true
    const { data, error } = await supabase.rpc('get_dashboard_summary', { p_is_agent: true })
    if (error) throw error
    summary.value = {
      unread_notifications: Number(data?.unread_notifications ?? 0),
      listing_count: Number(data?.listing_count ?? 0),
      total_payout: Number(data?.total_payout ?? 0),
      unread_messages: Number(data?.unread_messages ?? 0)
    }
  } catch (e) {
    console.warn('get_dashboard_summary failed:', e)
    summary.value = { unread_notifications: 0, listing_count: 0, total_payout: 0, unread_messages: 0 }
  } finally {
    isLoadingSummary.value = false
  }
}

/* ---------- listings + pagination ---------- */
const listings = ref([])
const isLoadingListings = ref(false)
const pageSize = ref(10)
const currentPage = ref(1)
const totalCount = ref(0)
const totalPages = computed(() => Math.max(1, Math.ceil((totalCount.value || 0) / pageSize.value)))
const pageStart = computed(() => (currentPage.value - 1) * pageSize.value)
const pageEnd = computed(() => Math.min(pageStart.value + pageSize.value, totalCount.value || 0))

async function fetchListings() {
  if (!user.value?.uid || !isAgent.value) {
    listings.value = []
    totalCount.value = 0
    return
  }
  try {
    isLoadingListings.value = true
    const from = (currentPage.value - 1) * pageSize.value
    const to = from + pageSize.value - 1

    const { data, count, error } = await supabase
      .from('listings')
      .select('*', { count: 'exact' })
      .eq('uid', user.value.uid)
      .order('created_at', { ascending: false })
      .range(from, to)

    if (error) throw error
    listings.value = data ?? []
    totalCount.value = count ?? listings.value.length
  } catch (e) {
    console.error('Error fetching listings:', e)
    listings.value = []
    totalCount.value = 0
  } finally {
    isLoadingListings.value = false
  }
}

/* ---------- steps (for chip) ---------- */
const steps = [
  { key: 'pending_posting', label: 'Pending Approval', color: 'yellow', icon: 'mdi-clock-time-nine-outline' },
  { key: 'listing_posted', label: 'Listing Posted', color: 'blue', icon: 'mdi-bullhorn' },
  { key: 'booking_requested', label: 'Booking Requested', color: 'amber', icon: 'mdi-account-clock' },
  { key: 'pending_confirmation', label: 'Pending Confirmation', color: 'indigo', icon: 'mdi-help-circle-outline' },
  { key: 'pending_payment', label: 'Pending Payment', color: 'deep-orange', icon: 'mdi-cash-clock' },
  { key: 'booking_confirmed', label: 'Booking Confirmed', color: 'green', icon: 'mdi-check-circle' },
  { key: 'final_payment', label: 'Final Payment', color: 'teal', icon: 'mdi-cash-check' },
  { key: 'set_sail', label: 'Set Sail', color: 'cyan', icon: 'mdi-ship-wheel' },
  { key: 'end_summary', label: 'End Summary', color: 'grey', icon: 'mdi-file-document-check' }
]
const stepByKey = new Map(steps.map((s, i) => [s.key, { ...s, index: i }]))

function stepFor(listing) {
  const s = listing?.current_step
  if (Number.isFinite(s)) {
    const idx = Math.min(Math.max(0, s), steps.length - 1)
    return steps[idx]
  }
  if (typeof s === 'string') {
    return stepByKey.get(s) ?? steps[0]
  }
  // fallback for legacy `status`
  if (typeof listing?.status === 'string') {
    return stepByKey.get(listing.status) ?? steps[0]
  }
  return steps[0]
}
function chipAttrsFor(listing) {
  const step = stepFor(listing)
  return { color: step.color }
}

/* ---------- helpers ---------- */
function formatCurrency(v) {
  const n = Number(v ?? 0)
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(n)
}
function formatDate(d) {
  if (!d) return '—'
  const dt = new Date(d)
  return isNaN(dt) ? '—' : dt.toLocaleDateString()
}
function payoutAmount(totalCost, serviceFee) {
  const t = Number(totalCost ?? 0)
  const f = Number(serviceFee ?? 0)
  return Math.max(0, t - f)
}

/* ---------- effects ---------- */
onMounted(() => {
  if (user.value?.uid) {
    loadSummary()
    fetchListings()
  }
})

watch(() => user.value?.uid, uid => {
  if (uid) {
    currentPage.value = 1
    loadSummary()
    fetchListings()
  } else {
    listings.value = []
    totalCount.value = 0
    summary.value = { unread_notifications: 0, listing_count: 0, total_payout: 0, unread_messages: 0 }
  }
}, { immediate: true })

watch([currentPage, pageSize], () => {
  fetchListings()
})
</script>

<style scoped>
.stat-card { transition: transform .15s ease, box-shadow .15s ease; }
.stat-card:hover { transform: translateY(-2px); }
.shadow-sm { box-shadow: 0 2px 8px rgba(0,0,0,.06); }
</style>
